import { useState, useCallback, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { format } from 'date-fns';
import { ArrowLeft, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, CreditCard } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { getCreditHistoryCache, setCreditHistoryCache, clearCreditHistoryCache } from '../lib/cache';
import { CheckoutButton } from '../components/CheckoutButton';
import type { CreditTransaction } from '../types';

interface CreditHistoryProps {
  setHasUnsavedChanges: (value: boolean) => void;
}

export function CreditHistory({ setHasUnsavedChanges }: CreditHistoryProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const itemsPerPage = 10;

  useEffect(() => {
    if (!user) return;

    async function fetchTransactions() {
      try {
        setLoading(true);
        setError(null);

        // Check cache first
        const cachedData = getCreditHistoryCache(user.id, currentPage);
        if (cachedData) {
          setTransactions(cachedData.transactions);
          setTotalTransactions(cachedData.totalCount);
          setTotalPages(Math.ceil(cachedData.totalCount / itemsPerPage));
          setLoading(false);

          // Fetch in background to update cache
          fetchFromSupabase();
          return;
        }

        await fetchFromSupabase();
      } catch (err) {
        console.error('Error fetching transactions:', err);
        setError('Failed to load transaction history');
        if (user) {
          clearCreditHistoryCache(user.id);
        }
      } finally {
        setLoading(false);
      }
    }

    async function fetchFromSupabase() {
      try {
        const { data, error } = await supabase.rpc('get_credit_transactions', {
          p_limit: itemsPerPage,
          p_offset: (currentPage - 1) * itemsPerPage,
          p_user_id: user.id
        });

        if (error) throw error;

        if (data && data.length > 0) {
          setTransactions(data);
          setTotalTransactions(data[0].total_count);
          setTotalPages(Math.ceil(data[0].total_count / itemsPerPage));

          // Update cache
          setCreditHistoryCache(user.id, currentPage, {
            transactions: data,
            totalCount: data[0].total_count,
            page: currentPage
          });
        } else {
          setTransactions([]);
          setTotalTransactions(0);
          setTotalPages(1);
        }
      } catch (err) {
        console.error('Error fetching transactions from Supabase:', err);
        setError('Failed to load transaction history from Supabase');
        throw err;
      }
    }

    fetchTransactions();
  }, [user, currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[--bg-secondary] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-[--text-secondary] hover:text-[--text-primary]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </button>
          <div className="mt-4 flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-[--text-primary]">
                Credit History
              </h1>
              <p className="mt-1 text-lg text-[--text-secondary]">
                Your current balance: <span className="font-semibold text-xl text-[--link-default]">{user.credits.toLocaleString()} credits</span>
              </p>
            </div>
            <CheckoutButton 
              priceId="price_1RL1yvCcUl7ct27BisAozASC" 
              className="flex items-center space-x-2 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800"
            >
              <CreditCard className="w-4 h-4" />
              <span>Buy Credits</span>
            </CheckoutButton>
          </div>
        </div>

        {error ? (
          <div className="bg-error-50 dark:bg-[rgb(var(--error-900)/0.2)] p-4 rounded-lg">
            <p className="text-error-600">{error}</p>
          </div>
        ) : loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="bg-[--bg-primary] p-4 rounded-lg animate-pulse">
                <div className="h-4 bg-[--bg-tertiary] rounded w-3/4"></div>
                <div className="mt-2 h-4 bg-[--bg-tertiary] rounded w-1/4"></div>
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="bg-[--bg-primary] shadow overflow-hidden sm:rounded-lg">
              <div className="divide-y divide-[--border-primary]">
                {transactions.map((transaction) => (
                  <div key={transaction.id} className="p-4">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center space-x-3">
                        <span className="text-sm font-medium text-[--text-primary]">
                          {transaction.description}
                        </span>
                        <span className={`text-sm ${
                          transaction.amount > 0 
                            ? 'text-success-600' 
                            : 'text-error-600'
                        } flex items-center`}>
                          {transaction.amount > 0 ? (
                            <ArrowUp className="w-4 h-4 mr-1" />
                          ) : (
                            <ArrowDown className="w-4 h-4 mr-1" />
                          )}
                          {Math.abs(transaction.amount)}
                        </span>
                      </div>
                      <span className="font-mono text-sm text-[--text-secondary]">
                        {format(new Date(transaction.created_at), 'MMM d, yyyy HH:mm')}
                      </span>
                    </div>
                  </div>
                ))}
                {transactions.length === 0 && (
                  <div className="p-8 text-center">
                    <p className="text-[--text-secondary]">No transactions found</p>
                  </div>
                )}
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-1 border border-[--border-secondary] text-xs font-medium rounded-md text-[--text-primary] bg-[--bg-primary] hover:bg-[--state-hover] disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-2 py-1 border border-[--border-secondary] text-xs font-medium rounded-md text-[--text-primary] bg-[--bg-primary] hover:bg-[--state-hover] disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="pl-2 text-xs text-[--text-primary]">
                      {Math.min((currentPage - 1) * itemsPerPage + 1, totalTransactions)}
                      &nbsp;to&nbsp;
                      {Math.min(currentPage * itemsPerPage, totalTransactions)}
                      &nbsp;of&nbsp;
                      {totalTransactions}
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-1 py-1 rounded-l-md border border-[--border-secondary] bg-[--bg-primary] text-xs font-medium text-[--text-secondary] hover:bg-[--state-hover] disabled:opacity-50"
                      >
                        <span className="sr-only">Previous</span>
                        <ChevronLeft className="h-3 w-3" />
                      </button>
                      {(() => {
                        let startPage = Math.max(1, currentPage - 2);
                        let endPage = Math.min(totalPages, startPage + 4);
                        
                        if (endPage - startPage < 4) {
                          startPage = Math.max(1, endPage - 4);
                        }
                        
                        return Array.from(
                          { length: endPage - startPage + 1 }, 
                          (_, i) => startPage + i
                        ).map((page) => (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`relative inline-flex items-center px-2 py-1 border text-xs font-medium ${
                              currentPage === page
                                ? "z-10 bg-[--state-selected] border-[--border-focus] text-[--link-default]"
                                : "bg-[--bg-primary] border-[--border-secondary] text-[--text-primary] hover:bg-[--state-hover]"
                            }`}
                          >
                            {page}
                          </button>
                        ));
                      })()}
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-1 py-1 rounded-r-md border border-[--border-secondary] bg-[--bg-primary] text-xs font-medium text-[--text-secondary] hover:bg-[--state-hover] disabled:opacity-50"
                      >
                        <span className="sr-only">Next</span>
                        <ChevronRight className="h-3 w-3" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}